/**
 * UTC-midnight countdown helper + DOM component for SNAKE-22 main screen.
 *
 * Pure helpers exported for unit tests; the class is a thin DOM wrapper
 * that ticks once per second.
 */

const LAST_HOUR_MS = 60 * 60 * 1000

export function msUntilNextUtcMidnight(now: Date): number {
  const next = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + 1,
    0,
    0,
    0,
    0,
  )
  return Math.max(0, next - now.getTime())
}

export function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`
}

export function isLastHour(ms: number): boolean {
  return ms < LAST_HOUR_MS
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

export class CountdownUtcMidnight {
  private el: HTMLDivElement | null = null
  private valueEl: HTMLSpanElement | null = null
  private timerId: number | null = null
  private nowProvider: () => Date = () => new Date()

  mount(parent: HTMLElement): HTMLElement {
    const el = document.createElement('div')
    el.style.cssText = `
      text-align: center; font-size: 0.85rem; letter-spacing: 0.05em;
      color: rgba(255,255,255,0.55); font-family: 'Inter', sans-serif;
    `
    const label = document.createElement('span')
    label.textContent = 'Сброс через '
    el.appendChild(label)

    const value = document.createElement('span')
    value.style.fontWeight = '600'
    el.appendChild(value)

    parent.appendChild(el)
    this.el = el
    this.valueEl = value
    this.refresh()
    return el
  }

  setNowProvider(fn: () => Date): void {
    this.nowProvider = fn
    this.refresh()
  }

  start(): void {
    this.stop()
    if (typeof window === 'undefined') return
    this.timerId = window.setInterval(() => this.refresh(), 1000)
  }

  stop(): void {
    if (this.timerId !== null) {
      window.clearInterval(this.timerId)
      this.timerId = null
    }
  }

  destroy(): void {
    this.stop()
    if (this.el) {
      this.el.remove()
      this.el = null
      this.valueEl = null
    }
  }

  private refresh(): void {
    if (!this.el || !this.valueEl) return
    const remaining = msUntilNextUtcMidnight(this.nowProvider())
    this.valueEl.textContent = formatCountdown(remaining)
    this.el.style.color = isLastHour(remaining) ? '#E74C3C' : 'rgba(255,255,255,0.55)'
  }
}
